// Option lists and one-line summaries for the Profile page (issue #86). Pure,
// so the wording of every summary row is unit-tested.
import { showWeight } from "../../utils/units";
import { clockToSeconds, secondsToClock } from "../../utils/clockInput";
import { resolveSegmentMode } from "../../utils/workoutPlanUtils";
import { benchmarkAge, isOneRmMetric, normalisedFigure } from "../../utils/benchmarkNormalise";
import {
  DEFAULT_REST_STYLE,
  DEFAULT_SESSION_PACE,
  REST_STYLE_OPTIONS,
  SESSION_PACE_OPTIONS,
} from "../../config/trainingPreferences";

export const FITNESS_LEVELS = [
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
];

export const TRAINING_EXPERIENCE = [
  { value: "beginner", label: "Under 6 months", short: "<6 mo" },
  { value: "novice", label: "6–12 months", short: "6–12 mo" },
  { value: "intermediate", label: "1–3 years", short: "1–3 yrs" },
  { value: "advanced", label: "3–5 years", short: "3–5 yrs" },
  { value: "expert", label: "5+ years", short: "5+ yrs" },
];

// The rep-time multiplier stored as profiles.training_data.pace. Called rep
// speed on screen so it can't be confused with Session pace.
export const REP_SPEEDS = [
  { value: "fast", label: "Fast", description: "You move briskly between reps.", aside: "×0.8" },
  { value: "standard", label: "Standard", description: "Typical rep timing.", aside: "×1.0" },
  { value: "controlled", label: "Controlled", description: "Slow, deliberate reps.", aside: "×1.2" },
];

export const LOCATION_TYPES = [
  { value: "home", label: "Home" },
  { value: "gym", label: "Gym" },
  { value: "outdoor", label: "Outdoor" },
  { value: "office", label: "Office" },
  { value: "other", label: "Other" },
];

export const BENCHMARK_KINDS = [
  { value: "strength", label: "Strength" },
  { value: "cardio", label: "Cardio" },
  { value: "power", label: "Power" },
  { value: "bodyweight", label: "Bodyweight" },
  { value: "other", label: "Other" },
];

export const BENCHMARK_METRICS = ["1RM", "Time", "Max reps", "Avg power"];

export const BENCHMARK_UNITS = [
  { value: "kg", label: "kg" },
  { value: "W", label: "watts" },
  { value: "reps", label: "reps" },
  { value: "cals", label: "cals" },
  { value: "m", label: "metres" },
  { value: "sec", label: "time" },
];

// What a timed result was for.
export const BENCHMARK_FOR_UNITS = [
  { value: "m", label: "metres" },
  { value: "cals", label: "cals" },
];

// Stored as the free-text `effort`, so older entries keep whatever they say.
// The maths reads them as reps in reserve (utils/benchmarkNormalise).
export const BENCHMARK_EFFORTS = ["All out", "Hard", "Moderate"];

/**
 * Which "for" field a result takes (issue #88): reps for a lift (none for a
 * straight 1RM), a distance or calories for a time, and a time for an amount.
 */
export const benchmarkTargetKind = (unit, metric) => {
  if (unit === "kg") return isOneRmMetric(metric) ? null : "reps";
  if (unit === "sec") return "amount";
  if (unit) return "time";
  return null;
};

const labelOf = (options, value) => options.find((o) => o.value === value)?.label;

export const ageFromBirthYear = (birthYear, now = new Date()) => {
  const year = Number(birthYear);
  return year > 1900 ? now.getFullYear() - year : null;
};

export const describeLevel = (profile, now) => {
  const age = ageFromBirthYear(profile.birthYear, now);
  const experience = TRAINING_EXPERIENCE.find(
    (o) => o.value === (profile.trainingExperience || "beginner"),
  );
  return [
    age != null ? String(age) : null,
    labelOf(FITNESS_LEVELS, profile.fitnessLevel),
    experience?.short,
  ]
    .filter(Boolean)
    .join(" · ");
};

export const describeDays = (profile) =>
  profile.weeklySessions ? `${profile.weeklySessions} days` : "Auto";

const SEGMENT_WORD = { off: "Off", auto: "Auto", on: "On" };

export const describeWarmCool = (profile) => {
  const part = (segment, durationKey) => {
    const mode = resolveSegmentMode(profile, segment);
    return mode === "on"
      ? `${profile[durationKey] || 5} min`
      : SEGMENT_WORD[mode];
  };
  return `${part("warmup", "warmupDuration")} · ${part("cooldown", "cooldownDuration")}`;
};

export const describeRest = (profile) =>
  `${labelOf(REST_STYLE_OPTIONS, profile.restStyle || DEFAULT_REST_STYLE)} · ${labelOf(
    SESSION_PACE_OPTIONS,
    profile.sessionPace || DEFAULT_SESSION_PACE,
  )}`;

/** Rest & pace plus rep speed, which share the Pace & rest sheet. Rep speed
 * shows only when it isn't Standard (or unknown, signed out). */
export const describePaceAndRest = (profile, repSpeed) => {
  const speed = REP_SPEEDS.find((o) => o.value === repSpeed);
  const rest = describeRest(profile);
  return speed && speed.value !== "standard" ? `${rest} · ${speed.label.toLowerCase()} reps` : rest;
};

export const describeHistory = (settings) =>
  settings.guidedSessionEnabled ?? true
    ? `Last ${settings.historyLookbackDays ?? 7} days`
    : "Off";

// The changeover hold (Settings' old Holds & rests), in a word or two.
const HOLD_SHORT = { wait: "wait", count: "count-in", off: "no hold" };

export const describeChangeovers = (settings) =>
  `${settings.defaultTransitionSeconds ?? 15} s · ${settings.defaultBlockSetupSeconds ?? 45} s · ${
    HOLD_SHORT[settings.changeoverHold] || HOLD_SHORT.wait
  }`;

export const describeRepSpeed = (pace) => {
  const speed = REP_SPEEDS.find((o) => o.value === pace) || REP_SPEEDS[1];
  return `${speed.label} ${speed.aside}`;
};

// NUMERIC values come back from pg as strings. Times are stored in seconds.
export const formatBenchmarkValue = (cap) => {
  if (cap.value == null || cap.value === "") return null;
  if (cap.unit === "sec") return secondsToClock(cap.value, { tenths: true });
  if (cap.unit === "kg") return showWeight(Number(cap.value));
  return cap.unit ? `${Number(cap.value)} ${cap.unit}` : String(Number(cap.value));
};

export const formatTempo = (cap) => {
  const tempo = cap.sec_per_rep != null ? Number(cap.sec_per_rep) : null;
  return Number.isFinite(tempo) && tempo > 0 ? `${tempo} s/rep` : null;
};

// "for 2000 m" or "in 1:05"; reps go next to the load instead.
const formatTarget = (cap) => {
  const n = Number(cap.target_value);
  if (!cap.target_unit || !(n > 0) || cap.target_unit === "reps") return null;
  return cap.target_unit === "sec" ? `in ${secondsToClock(n, { tenths: true })}` : `for ${n} ${cap.target_unit}`;
};

/** The figure shown at the end of a benchmark row, e.g. "100 kg × 5". */
export const benchmarkFigure = (cap) => {
  const value = formatBenchmarkValue(cap);
  if (!value) return formatTempo(cap);
  const reps = Number(cap.target_value);
  return cap.target_unit === "reps" && reps > 0 ? `${value} × ${reps}` : value;
};

/** The line under a benchmark's name, normalised figure first so it survives truncation. */
export const benchmarkDetail = (cap, now = new Date()) =>
  [
    normalisedFigure(cap),
    benchmarkAge(cap, now)?.stale ? "may be out of date" : null,
    cap.metric,
    formatTarget(cap),
    cap.effort,
    formatBenchmarkValue(cap) ? formatTempo(cap) : null,
    cap.notes,
  ]
    .filter(Boolean)
    .join(" · ");

/** Page preview of the first few benchmarks, e.g. "Back Squat 140 kg · …". */
export const previewBenchmarks = (caps, count = 3) =>
  caps
    .slice(0, count)
    .map((c) => [c.label, benchmarkFigure(c)].filter(Boolean).join(" "))
    .join(" · ");

/**
 * Benchmarks grouped by kind, in the kinds' order, with uncategorised ones
 * last. `query` matches the name, metric or notes.
 */
export const groupBenchmarks = (caps, query = "") => {
  const q = query.trim().toLowerCase();
  const hits = q
    ? caps.filter((c) =>
        [c.label, c.metric, c.notes].some((s) => String(s || "").toLowerCase().includes(q)),
      )
    : caps;
  const groups = BENCHMARK_KINDS.map((k) => ({
    key: k.value,
    label: k.label,
    items: hits.filter((c) => c.category === k.value),
  }));
  groups.push({
    key: "none",
    label: "No kind",
    items: hits.filter((c) => !BENCHMARK_KINDS.some((k) => k.value === c.category)),
  });
  return groups.filter((g) => g.items.length > 0);
};

export const describePlace = (place, equipmentPresets = []) => {
  const kit = equipmentPresets.find((p) => p.id === place.equipment_preset_id);
  return [labelOf(LOCATION_TYPES, place.location_type), kit?.name, place.address]
    .filter(Boolean)
    .join(" · ");
};

export const describeEquipmentSet = (set) => {
  const items = Array.isArray(set.equipment) ? set.equipment : [];
  const head = items.slice(0, 3).join(", ");
  return `${items.length} item${items.length === 1 ? "" : "s"}${
    head ? ` · ${head}${items.length > 3 ? "…" : ""}` : ""
  }`;
};

const positive = (raw, what) => {
  const trimmed = String(raw).trim();
  if (trimmed === "") return { value: null };
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return { error: `${what} must be a number.` };
  if (n <= 0) return { error: `${what} must be more than 0.` };
  return { value: n };
};

// The API row for a form, or { error }. Either a result or a per-rep tempo is
// needed; a tempo-only row carries no unit.
export const benchmarkFromForm = (form) => {
  if (!form.label.trim()) return { error: "Give it a name." };
  let value;
  if (form.unit === "sec") {
    const raw = form.valueClock.trim();
    const seconds = raw === "" ? null : clockToSeconds(raw);
    if (raw !== "" && seconds == null) return { error: "Enter the time as mm:ss." };
    if (seconds != null && seconds <= 0) return { error: "The time must be more than 0." };
    value = { value: seconds };
  } else {
    value = positive(form.valueNumber, "The result");
  }
  if (value.error) return value;
  const tempo = positive(form.secPerRep, "Seconds per rep");
  if (tempo.error) return tempo;
  if (value.value == null && tempo.value == null) {
    return { error: "Add a result or seconds per rep." };
  }
  let target = { value: null, unit: null };
  const kind = value.value != null ? benchmarkTargetKind(form.unit, form.metric) : null;
  if (kind === "reps") {
    const reps = positive(form.reps, "Reps");
    if (reps.error) return reps;
    if (reps.value != null && reps.value !== Math.round(reps.value)) {
      return { error: "Reps must be a whole number." };
    }
    target = { value: reps.value, unit: reps.value != null ? "reps" : null };
  } else if (kind === "amount") {
    const amount = positive(form.forAmount, "The distance or calories");
    if (amount.error) return amount;
    target = { value: amount.value, unit: amount.value != null ? form.forUnit : null };
  } else if (kind === "time") {
    const raw = form.inClock.trim();
    const seconds = raw === "" ? null : clockToSeconds(raw);
    if (raw !== "" && !(seconds > 0)) return { error: "Enter how long as mm:ss." };
    target = { value: seconds, unit: seconds != null ? "sec" : null };
  }
  return {
    data: {
      label: form.label.trim(),
      metric: form.metric.trim() || null,
      value: value.value,
      unit: value.value != null ? form.unit || null : null,
      secPerRep: tempo.value,
      effort: form.effort.trim() || null,
      category: form.category || null,
      notes: form.notes.trim() || null,
      targetValue: target.value,
      targetUnit: target.unit,
      testedOn: form.testedOn || null,
    },
  };
};
